import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '../../routing';
import { adminApi, commandCenterApi } from '../../utils/api';
import { toast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { UserModal } from './modals';
import { Capability, InvitePill, LastLogin, LoadState, RoleBadge, formatDate, useRefetchOnFocus } from './parts';
import { useConfirm } from '../../utils/useConfirm';

// כרטיס משתמש — the answer to "why can't Dana see restaurant X?", and the
// reason impersonation can wait for a later cut.

/**
 * Role → what this person can actually DO, in the product's own words.
 *
 * This is a SYNC PAIR with server/lib/roles.js (and mirrors the flags
 * AuthContext already derives). Change the roles matrix there → change it here.
 * Exported so the tests pin every role against the server's rules.
 */
export function capabilitiesFor(role) {
  const isAdmin = role === 'admin';
  const canManage = isAdmin || role === 'hq_admin' || role === 'manager';
  return [
    { label: 'רואה נתונים',      allowed: true },                 // every role reads
    { label: 'רושם תשלומים',     allowed: canManage },
    { label: 'עורך תנאי תשלום',  allowed: canManage },
    { label: 'רואה הנחות',       allowed: canManage },
    { label: 'מאשר טיוטות',      allowed: isAdmin },
    { label: 'מעלה מסמכים',      allowed: isAdmin },
  ];
}

export default function UserCardPage() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [askConfirm, confirmDialog] = useConfirm();
  const [restaurants, setRestaurants] = useState([]);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(false);
    try {
      const [u, r] = await Promise.all([
        commandCenterApi.getPerson(userId),
        adminApi.getRestaurants(),
      ]);
      setUser(u.data);
      setRestaurants(Array.isArray(r.data) ? r.data : []);
    } catch {
      setError(true);
      setUser(null);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  useEffect(() => {
    if (user?.email) document.title = `${user.email} · Command Center`;
  }, [user]);

  const resend = async () => {
    try {
      await commandCenterApi.resendInvite(user.id);
      toast(`נשלח קישור להגדרת סיסמה ל-${user.email}`);
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שליחת המייל נכשלה', 'error');
    }
  };

  const remove = () => askConfirm({
    title: 'מחיקת משתמש',
    message: `למחוק את המשתמש ${user.email}?`,
    detail: 'המשתמש יאבד גישה למערכת. רישומים שיצר נשמרים בשמו.',
    confirmLabel: 'מחק משתמש',
    tone: 'danger',
    onConfirm: async () => {
      try {
        await adminApi.deleteUser(user.id);
        toast('המשתמש נמחק');
        navigate('/command-center/people');
      } catch (err) {
        toast(err.response?.data?.error || 'שגיאה', 'error');
      }
    },
  });

  return (
    <>
      <div className="cc-crumb">
        <Link to="/command-center/people">משתמשים</Link>
        <Icon name="chevron-inline" size={13} className="cc-muted" />
        <span>{user?.email || '…'}</span>
      </div>

      <LoadState loading={!user && !error} error={error} onRetry={load} empty={false}>
        {user && (
          <>
            <div className="cc-card">
              <div className="cc-card-head" style={{ marginBottom: 0 }}>
                <div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{user.email}</div>
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <RoleBadge role={user.role} />
                    <span className="cc-muted" style={{ fontSize: '0.82rem' }}>
                      נוצר {formatDate(user.created_at)}
                    </span>
                    {/* Same fact as the list column, so the two screens can't
                        tell different stories about whether someone got in. */}
                    <span className="cc-muted" style={{ fontSize: '0.82rem' }}>
                      התחברות אחרונה: <LastLogin value={user.last_login_at} />
                    </span>
                    <InvitePill invite={user.invite} />
                  </div>
                </div>
                <div className="cc-spacer" />
                {user.invite?.state !== 'active' && (
                  <button className="btn btn-sm btn-secondary" onClick={resend}>
                    <Icon name="send" size={14} /> שלח הזמנה מחדש
                  </button>
                )}
                <button className="btn btn-sm btn-primary" onClick={() => setEditing(true)}>
                  <Icon name="edit" size={14} /> עריכה
                </button>
              </div>
            </div>

            <div className="cc-card">
              <div className="cc-section-label">הרשאות — מה המשתמש הזה יכול לעשות</div>
              <div className="cc-caps">
                {capabilitiesFor(user.role).map(c => (
                  <Capability key={c.label} label={c.label} allowed={c.allowed} />
                ))}
              </div>
            </div>

            <div className="cc-card">
              <div className="cc-section-label">
                מסעדות {user.role === 'admin' ? '' : `· ${user.restaurants?.length || 0}`}
              </div>
              {user.role === 'admin' ? (
                // An admin's empty list means "all restaurants", not "none".
                // Leaving that implicit is itself a support call.
                <div style={{ fontSize: '0.88rem' }}>כל המסעדות (אדמין)</div>
              ) : user.restaurants?.length ? (
                <div className="table-wrapper">
                  <table>
                    <tbody>
                      {user.restaurants.map(r => (
                        <tr key={r.id} className="cc-row-link"
                          onClick={() => navigate(`/command-center/customers/${r.id}`)}>
                          <td>
                            <Icon name="store" size={14} className="cc-muted" />{' '}
                            {r.name}
                            {String(r.id) === String(user.restaurant_id) && (
                              <span className="cc-pill" style={{ marginInlineStart: 8 }}>
                                ברירת מחדל בהתחברות
                              </span>
                            )}
                          </td>
                          <td className="cc-go" style={{ width: 24 }}><Icon name="chevron-inline" size={14} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="cc-empty">אין מסעדות משויכות — המשתמש לא יראה דבר</div>
              )}
            </div>

            {/* True and non-obvious, and ops will hit it in the first week: the
                server re-reads the role from the DB on every request, while the
                browser caches the user and only refetches on page load. Writing
                it into the screen is cheaper than answering it forever. */}
            <div className="cc-notice">
              <Icon name="info" size={16} />
              <span>
                שינוי תפקיד או מסעדות נכנס לתוקף בשרת מיד, אבל הדפדפן של המשתמש
                מרענן את הפרטים רק בטעינת דף — בקש/י ממנו לרענן.
              </span>
            </div>

            <div className="cc-card" style={{ marginTop: 12 }}>
              <div className="cc-danger-zone">
                <Icon name="trash" size={15} style={{ color: '#b91c1c' }} />
                <span>מחיקת המשתמש היא לצמיתות ומבטלת את הגישה שלו מיד.</span>
                <span className="cc-spacer" />
                <button className="btn btn-sm btn-danger" onClick={remove}>מחק משתמש</button>
              </div>
            </div>
          </>
        )}
      </LoadState>

      {editing && (
        <UserModal
          user={user}
          restaurants={restaurants}
          onSave={() => { setEditing(false); load(); }}
          onClose={() => setEditing(false)}
        />
      )}
      {confirmDialog}
    </>
  );
}
